import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom'; // Import useNavigate and useLocation
import apiService from '../services/api';
import { useAppContext } from '../context/AppContext';
import { debounce } from 'lodash'; // Import debounce
import CopyableId from '../components/common/CopyableId'; // Import shared component
import SaveButton from '../components/common/SaveButton'; // Import save button component
import GenreFilterModal from '../components/common/GenreFilterModal'; // Import the modal
import LanguageFilterModal from '../components/common/LanguageFilterModal'; // Import the new modal
import GameDateFilterModal from '../components/common/GameDateFilterModal';
import GameSeasonFilterModal from '../components/common/GameSeasonFilterModal';
import { getLanguageColors } from '../config'; // Import language color helper
// Removed PAGE_SIZE_OPTIONS

const MainSearchResults = () => {
  const containerRef = useRef(null); // Ref for the main container
  const tableHeaderRef = useRef(null); // Ref for the table header section
  const paginationRef = useRef(null); // Ref for the pagination controls
  const columnMenuRef = useRef(null);

  // --- Constants ---
  const ROW_ESTIMATED_HEIGHT = 58; // Estimated height of a table row in pixels (adjust as needed)
  const MIN_PAGE_SIZE = 5; // Minimum number of results per page
  const DEBOUNCE_DELAY = 150; // Debounce delay for resize events in ms

  const navigate = useNavigate(); // Initialize navigate hook
  const location = useLocation(); // Initialize useLocation hook

  // Get search params and navigation function from context
  const {
    // mainSearchTerm, // No longer directly from context, will be derived from URL
    // mainSearchProgramType, // No longer directly from context, will be derived from URL
    // mainSearchRootId, // No longer directly from context, will be derived from URL
    navigateToComparePrograms,
    // Get search results state and setters from context
    mainSearchResultsPage,
    setMainSearchResultsPage,
    mainSearchResultsPageSize,
    setMainSearchResultsPageSize,
    mainSearchResultsSelected,
    setMainSearchResultsSelected,
    isDebugMode, // Get debug mode state from context
    searchFilters, // Get search filters from context to listen for changes
    t // Get translation function
  } = useAppContext();

  // Local state for search parameters, initialized from URL
  const [searchTerm, setSearchTerm] = useState('');
  const [programType, setProgramType] = useState('ALL');
  const [rootId, setRootId] = useState(null);
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [gameDate, setGameDate] = useState('');
  const [season, setSeason] = useState('');
  const [availableSeasons, setAvailableSeasons] = useState([]);

  // Use context state for page, size, and selections
  const currentPage = mainSearchResultsPage;
  const setCurrentPage = setMainSearchResultsPage; // Use context setter
  const pageSize = mainSearchResultsPageSize;
  const setPageSize = setMainSearchResultsPageSize; // Use context setter
  const selectedPrograms = mainSearchResultsSelected;
  const setSelectedPrograms = setMainSearchResultsSelected; // Use context setter

  // Local state for results, totals, loading, error
  const [results, setResults] = useState([]); // Keep local state for results
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [isLoading, setIsLoading] = useState(false); // Keep loading local
  const [error, setError] = useState(null);
  const [isGenreModalOpen, setIsGenreModalOpen] = useState(false); // State for genre modal visibility
  const [genreFilterIds, setGenreFilterIds] = useState([]); // State for selected genre IDs
  const [isDescLangModalOpen, setIsDescLangModalOpen] = useState(false); // State for desc lang modal
  const [descLangFilter, setDescLangFilter] = useState([]); // State for selected desc langs
  const [isTitleLangModalOpen, setIsTitleLangModalOpen] = useState(false); // State for title lang modal
  const [titleLangFilter, setTitleLangFilter] = useState([]); // State for selected title langs
  const [isGameDateModalOpen, setIsGameDateModalOpen] = useState(false);
  const [isSeasonModalOpen, setIsSeasonModalOpen] = useState(false);
  const [searchMode, setSearchMode] = useState('');
  const [hiddenColumns, setHiddenColumns] = useState(() => new Set(['updateId']));
  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);

  // Effect to parse URL parameters on component mount or URL change
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSearchTerm(params.get('term') || '');
    setProgramType(params.get('type') || 'ALL');
    setRootId(params.get('rootId') || null);
    setYearFrom(params.get('yearFrom') || '');
    setYearTo(params.get('yearTo') || '');
    setTitleLangFilter(params.get('titleLang') ? [params.get('titleLang')] : []);
    setDescLangFilter(params.get('descLang') ? [params.get('descLang')] : []);
    setGenreFilterIds(params.get('genreIds') ? params.get('genreIds').split(',').map(Number) : []); // Parse genreIds from URL
    setGameDate(params.get('gameDate') || '');
    setSeason(params.get('season') || '');
    setSearchMode(params.get('searchMode') || '');
    setCurrentPage(1); // Always reset to page 1 when URL parameters change
    setMainSearchResultsSelected([]); // Clear selections
  }, [location.search, setCurrentPage, setMainSearchResultsSelected]);

  // Effect to sync with context searchFilters when they change (from RealTimeSearch in header)
  useEffect(() => {
    // Only update if we're not in the middle of parsing URL parameters
    // This prevents conflicts between URL parsing and context updates
    if (searchFilters.yearFrom !== yearFrom) {
      setYearFrom(searchFilters.yearFrom || '');
    }
    if (searchFilters.yearTo !== yearTo) {
      setYearTo(searchFilters.yearTo || '');
    }
    if (searchFilters.titleLanguage !== 'all' && titleLangFilter[0] !== searchFilters.titleLanguage) {
      setTitleLangFilter(searchFilters.titleLanguage ? [searchFilters.titleLanguage] : []);
    }
    if (searchFilters.descriptionLanguage !== 'all' && descLangFilter[0] !== searchFilters.descriptionLanguage) {
      setDescLangFilter(searchFilters.descriptionLanguage ? [searchFilters.descriptionLanguage] : []);
    }
    if ((searchFilters.searchMode || '') !== searchMode) {
      setSearchMode(searchFilters.searchMode || '');
    }
  }, [searchFilters, yearFrom, yearTo, titleLangFilter, descLangFilter, searchMode]); // Add back dependencies but with proper checks above


  const TOGGLEABLE_COLUMNS = [
    { key: 'genres',    label: 'Genres' },
    { key: 'type',      label: 'Type' },
    { key: 'subType',   label: 'Sub Type' },
    { key: 'year',      label: 'Year' },
    { key: 'descLang',  label: 'Desc Lang' },
    { key: 'titleLang', label: 'Title Lang' },
    { key: 'updateId',  label: 'Update ID' },
  ];

  const toggleColumn = (col) => {
    setHiddenColumns(prev => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col); else next.add(col);
      return next;
    });
  };

  useEffect(() => {
    const handler = (e) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(e.target))
        setIsColumnMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Function to fetch paginated results
  const fetchPaginatedResults = useCallback(async (page, currentGenreIds, currentDescLangs, currentTitleLangs, debug, currentYearFrom, currentYearTo, currentGameDate, currentSeason, currentSearchMode) => { // Accept all filters + debug + year filters + searchMode
    // Ensure pageSize is calculated before fetching
    if (!pageSize || pageSize <= 0) {
        // console.log("Page size not determined yet, skipping fetch.");
        return;
    }
    // If rootId is present, searchTerm might be empty, so we proceed.
    // If rootId is NOT present, then searchTerm is required.
    if (!rootId && !searchTerm) {
      setResults([]);
      setTotalPages(0);
      setTotalResults(0);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      // Determine search parameters based on whether rootId is set
      let apiSearchTerm = searchTerm;
      let apiRootId = rootId; // Use rootId from local state

      if (apiRootId) {
        apiSearchTerm = ''; // If rootId is used, the primary search term is the rootId itself
                               // The 'value' field in the API can be empty or a placeholder
       }
 
       // Use pageSize state and all filters in the API call
       const response = await apiService.search(
         apiSearchTerm, // This will be empty if rootId is used
         programType,   // programType can still be used as a filter
         page,
         pageSize,
        currentGenreIds,
        currentDescLangs,
        currentTitleLangs,
        false, // castMember - removed
        false, // crewMember - removed
        debug, // Pass debug flag
        currentYearFrom, // Pass yearFrom
        currentYearTo, // Pass yearTo
        apiRootId, // Pass the rootId to the API service
        currentGameDate,
        currentSeason,
        currentSearchMode // Pass searchMode (exact/startsWith)
      );

      // Extract data and pagination details from the response structure { data: [], pagination: { ... } }
      setResults(response.data || []);
      if (programType === 'Sports event' && response.data) {
        const uniqueSeasons = [...new Set(response.data.map(item => item.season).filter(Boolean))];
        setAvailableSeasons(uniqueSeasons);
      }
      if (response.pagination) {
        setTotalResults(response.pagination.total || 0);
        setTotalPages(response.pagination.total_pages || 0);
        // setCurrentPage(response.pagination.page || page); // REMOVED: currentPage is managed by handlePageChange
      } else {
        // Fallback if pagination object is missing
        console.warn("Pagination object missing in API response");
        setTotalResults(response.data?.length || 0); // Estimate total based on current data
        setTotalPages(1); // Assume only one page
        setCurrentPage(1);
      }

    } catch (err) {
      console.error('Error fetching paginated search results:', err);
      setError(`Failed to load search results for "${rootId || searchTerm}".`); // More specific error
      setResults([]);
      setTotalPages(0);
      setTotalResults(0);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, programType, pageSize, setCurrentPage, rootId]); // Removed filter dependencies that change frequently

  // Debounced version of fetch function to prevent rapid API calls
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(debounce((page, genreIds, descLangs, titleLangs, debug, yearFrom, yearTo, gameDate, season, searchMode) => {
    fetchPaginatedResults(page, genreIds, descLangs, titleLangs, debug, yearFrom, yearTo, gameDate, season, searchMode);
  }, 300), [fetchPaginatedResults]); // 300ms debounce delay

  // --- Dynamic Page Size Calculation ---
  const calculateAndSetPageSize = useCallback(() => {
    if (!containerRef.current || !tableHeaderRef.current) {
      // console.log("Refs not ready for calculation");
      return; // Don't calculate if refs aren't set yet
    }

    // Get heights and positions
    const windowHeight = window.innerHeight;
    // const containerTop = containerRef.current.offsetTop; // Space above the main content area - Not needed if using windowHeight directly
    const tableHeaderBottom = tableHeaderRef.current.getBoundingClientRect().bottom; // Use getBoundingClientRect for more accuracy
    const paginationHeight = paginationRef.current ? paginationRef.current.offsetHeight : 50; // Estimate if not rendered yet
    const containerPadding = 48; // Account for p-4/md:p-6 (use larger value for safety)
    const extraBuffer = 20; // Extra buffer space

    // Calculate available height for the table body
    const availableHeight = windowHeight - tableHeaderBottom - paginationHeight - containerPadding - extraBuffer;

    // console.log(`WindowH: ${windowHeight}, TableHeaderBottom: ${tableHeaderBottom}, PaginationH: ${paginationHeight}, AvailableH: ${availableHeight}`);

    if (availableHeight > 0) {
      const calculatedSize = Math.floor(availableHeight / ROW_ESTIMATED_HEIGHT);
      const newPageSize = Math.max(MIN_PAGE_SIZE, calculatedSize); // Ensure minimum size
      // console.log(`Calculated Size: ${calculatedSize}, New Page Size: ${newPageSize}, Current Page Size: ${pageSize}`);

      // Only update if the size actually changes to prevent unnecessary fetches
      if (newPageSize !== pageSize) {
        // console.log(`Setting new page size: ${newPageSize}`);
        setPageSize(newPageSize);
        // Reset to page 1 only if page size changes *after* initial load
        if (pageSize > 0) { // Avoid resetting on initial calculation before first fetch
            setCurrentPage(1);
        }
      }
    } else {
      // Fallback if calculation is off
      if (pageSize !== MIN_PAGE_SIZE) {
        // console.log(`Available height too small, setting minimum page size: ${MIN_PAGE_SIZE}`);
        setPageSize(MIN_PAGE_SIZE);
        if (pageSize > 0) {
            setCurrentPage(1);
        }
      }
    }
  }, [pageSize, setPageSize, setCurrentPage]); // Dependencies

  // Debounced version of the calculation function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedCalculateSize = useCallback(debounce(calculateAndSetPageSize, DEBOUNCE_DELAY), [calculateAndSetPageSize]);

  // Effect to calculate size on mount and resize
  useEffect(() => {
    // Initial calculation after a short delay for layout stabilization
    const timer = setTimeout(calculateAndSetPageSize, 150); // Increased delay slightly

    // Add resize listener
    window.addEventListener('resize', debouncedCalculateSize);

    // Cleanup
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', debouncedCalculateSize);
      debouncedCalculateSize.cancel(); // Cancel any pending debounced calls
    };
    // Ensure this runs only once on mount by passing calculateAndSetPageSize and debouncedCalculateSize
  }, [calculateAndSetPageSize, debouncedCalculateSize]);

  // Function to update URL with current search parameters
  const updateURL = useCallback(() => {
    const params = new URLSearchParams();
    
    if (searchTerm) params.set('term', searchTerm);
    if (programType && programType !== 'ALL') params.set('type', programType);
    if (rootId) params.set('rootId', rootId);
    if (yearFrom) params.set('yearFrom', yearFrom);
    if (yearTo) params.set('yearTo', yearTo);
    if (gameDate) params.set('gameDate', gameDate);
    if (season) params.set('season', season);
    if (titleLangFilter.length > 0) params.set('titleLang', titleLangFilter[0]); // Assuming single selection for URL
    if (descLangFilter.length > 0) params.set('descLang', descLangFilter[0]); // Assuming single selection for URL
    if (genreFilterIds.length > 0) params.set('genreIds', genreFilterIds.join(','));
    
    const newURL = `/search-results?${params.toString()}`;
    
    // Only update if URL actually changed to avoid unnecessary history entries
    if (window.location.pathname + window.location.search !== newURL) {
      navigate(newURL, { replace: true }); // Use replace to avoid creating new history entries
    }
  }, [searchTerm, programType, rootId, yearFrom, yearTo, titleLangFilter, descLangFilter, genreFilterIds, gameDate, season, navigate]);

  // Effect to update URL when search parameters change (but not on initial load)
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  useEffect(() => {
    if (!isInitialLoad) {
      updateURL();
    }
  }, [updateURL, isInitialLoad]);

  // Effect to fetch results when relevant parameters change
  useEffect(() => {
    // Fetch data if pageSize is determined AND (either searchTerm or rootId is present)
    if (pageSize > 0 && (searchTerm || rootId)) {
      // Use debounced fetch for filter changes, direct fetch for page changes
      if (currentPage === 1) {
        // Use debounced fetch for filter changes (when page resets to 1)
        debouncedFetch(currentPage, genreFilterIds, descLangFilter, titleLangFilter, isDebugMode, yearFrom, yearTo, gameDate, season, searchMode);
      } else {
        // Use direct fetch for page changes (immediate response)
        fetchPaginatedResults(currentPage, genreFilterIds, descLangFilter, titleLangFilter, isDebugMode, yearFrom, yearTo, gameDate, season, searchMode);
      }

      // Mark that initial load is complete after first fetch
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    } else if (!searchTerm && !rootId) {
        // Clear results if both search term and rootId are absent
        setResults([]);
        setTotalPages(0);
        setTotalResults(0);
    }
    // This effect depends on the fetch function and current page - other dependencies are handled by fetchPaginatedResults
  }, [fetchPaginatedResults, debouncedFetch, currentPage, genreFilterIds, descLangFilter, titleLangFilter, isDebugMode, yearFrom, yearTo, gameDate, season, searchMode, isInitialLoad]);

  // --- Event Handlers ---

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      // Optional: Scroll table container to top when page changes
      const tableContainer = containerRef.current?.querySelector('.overflow-y-auto');
      if (tableContainer) {
        tableContainer.scrollTop = 0;
      }
    }
  };

  // Handle applying genres from the modal
  const handleApplyGenres = (selectedIds) => {
    setGenreFilterIds(selectedIds); // Update the state with selected IDs
    setCurrentPage(1); // Reset to page 1 when filters change
    setIsGenreModalOpen(false); // Close the modal
  };

  // Handle applying description languages from the modal
  const handleApplyDescLangs = (selectedLangs) => {
    setDescLangFilter(selectedLangs);
    setCurrentPage(1);
    setIsDescLangModalOpen(false);
  };

  // Handle applying title languages from the modal
  const handleApplyTitleLangs = (selectedLangs) => {
    setTitleLangFilter(selectedLangs);
    setCurrentPage(1);
    setIsTitleLangModalOpen(false);
  };

  const handleApplyGameDate = (date) => {
    setGameDate(date);
    setCurrentPage(1);
  };

  const handleApplySeason = (season) => {
    setSeason(season);
    setCurrentPage(1);
  };

  // Removed handlePageSizeChange as it's now dynamic

  // Handle checkbox change
  const handleCheckboxChange = (event, tmsId) => {
    if (event.target.checked) {
      setSelectedPrograms(prev => [...prev, tmsId]);
    } else {
      setSelectedPrograms(prev => prev.filter(id => id !== tmsId));
    }
  };

  // --- Pagination Logic ---
  const getPaginationRange = () => {
    const range = [];
    const totalPageNumbersToShow = 8; // How many page numbers to display (like the example)
    const halfRange = Math.floor(totalPageNumbersToShow / 2);

    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, currentPage + halfRange - (totalPageNumbersToShow % 2 === 0 ? 1 : 0)); // Adjust end based on even/odd total

    // Adjust range if near the beginning
    if (currentPage <= halfRange) {
      endPage = Math.min(totalPages, totalPageNumbersToShow);
    }

    // Adjust range if near the end
    if (currentPage + halfRange > totalPages) {
      startPage = Math.max(1, totalPages - totalPageNumbersToShow + 1);
    }

    // Ensure startPage is never less than 1
    startPage = Math.max(1, startPage);

    for (let i = startPage; i <= endPage; i++) {
      range.push(i);
    }
    return range;
  };
  // --- End Pagination Logic ---


  // Handle clicking on a result row or image/title to navigate to program details page
  const handleResultClick = (result) => {
    if (result && result.TMSId) {
      navigate(`/program/${result.TMSId}`); // Use navigate to go to the details page
    } else {
      console.error("Clicked result is missing TMSId:", result);
    }
  };

  const hasConnectorIds = results.some(r => r.connectorId);

  return (
    // Added ref to the main container, ensure it takes full height
    <div className="p-4 md:p-6 flex flex-col h-[calc(100vh-theme(space.16))] overflow-hidden" ref={containerRef}>
      {/* Adjusted height calculation based on potential header height */}
      <h1 className="text-2xl font-semibold mb-4 shrink-0"> {/* Added shrink-0 */}
        {t('searchResults.title', { term: rootId ? t('searchResults.rootId', { id: rootId }) : searchTerm })}
        {(programType !== 'ALL' && programType !== 'Root ID') && ` (${t('searchResults.type', { type: programType })})`}
        {(yearFrom || yearTo) && ` (${t('searchResults.year', { from: yearFrom, to: yearTo })})`}
        {titleLangFilter.length > 0 && ` (${t('searchResults.titleLang', { lang: titleLangFilter.join(', ') })})`}
        {descLangFilter.length > 0 && ` (${t('searchResults.descLang', { lang: descLangFilter.join(', ') })})`}
      </h1>

      {/* Top Controls: Compare Button */}
      {/* Added ref to this section */}
      <div className="flex justify-between items-center mb-4 shrink-0" ref={tableHeaderRef}> {/* Added shrink-0 */}
        {/* Compare Button (conditional) */}
        <div className="flex-1"> {/* Added flex-1 to allow button to take space */}
          {selectedPrograms.length >= 2 && (
            <button
              className="px-4 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => navigateToComparePrograms(selectedPrograms.join(','))}
              disabled={selectedPrograms.length < 2} // Disable if less than 2 selected
            >
              {t('searchResults.compare', { count: selectedPrograms.length })}
            </button>
          )}
        </div>
        {/* Column visibility toggle */}
        <div className="relative" ref={columnMenuRef}>
          <button
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1.5 text-gray-600"
            onClick={() => setIsColumnMenuOpen(o => !o)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Columns
          </button>
          {isColumnMenuOpen && (
            <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-20 min-w-max py-1">
              {TOGGLEABLE_COLUMNS.map(col => (
                <label key={col.key} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm text-gray-700 select-none">
                  <input
                    type="checkbox"
                    checked={!hiddenColumns.has(col.key)}
                    onChange={() => toggleColumn(col.key)}
                    className="form-checkbox h-3.5 w-3.5 text-blue-600 border-gray-300 rounded"
                  />
                  {col.label}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Loading Indicator */}
      {isLoading && (
        <div className="flex justify-center items-center p-8 flex-grow"> {/* Added flex-grow */}
          <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="ml-3 text-gray-600">{t('searchResults.loading')}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="text-center text-red-600 bg-red-100 p-4 rounded border border-red-300 shrink-0"> {/* Added shrink-0 */}
          {/* Note: error message might come from backend or be hardcoded in fetchPaginatedResults catch block. 
              Ideally we should use a key there too, but for now we display what we have or generic error if we want to strict localize.
              The catch block sets error with a template string. I should probably change that to just set an error code or key if possible, 
              or simply use the generic error key here if error is present. 
              However, the variable 'error' holds the message. 
              I'll leave {error} as is for now, as it is dynamic, but I should update where setError is called to use t() as well.
              Wait, I can't use t() easily inside useCallback if I don't include it in deps.
              Actually I can just use t('searchResults.error', { term: rootId || searchTerm }) here instead of {error} if I want to be consistent.
              Let's check how error is set: setError(`Failed to load search results for "${rootId || searchTerm}".`);
              Yes, I can replace {error} with t call here if error is truthy, but error might be something else.
              For now I will leave {error} but I'll update the setError call in the next tool use if needed.
              Actually, I can just use the key here if I assume the error is always the search error.
              Let's update the setError call site instead? No, I can't update two files at once.
              I'll update the setError call site in this file in a separate block.
          */}
          {error}
        </div>
      )}

      {/* No Results Message */}
      {!isLoading && !error && results.length === 0 && (searchTerm || rootId) && (
        <div className="text-center text-gray-500 p-4 flex-grow"> {/* Added flex-grow */}
          {t('searchResults.noResults')}
        </div>
      )}

      {/* Results Display */}
      {!isLoading && !error && results.length > 0 && (
        // Use flex-grow to make this section fill remaining space
        <> {/* Fragment starts here */}
          <div className="flex flex-col flex-grow overflow-hidden"> {/* Added flex flex-col flex-grow overflow-hidden */}
            {/* Results Info */}
            <div className="mb-4 text-sm text-gray-600 shrink-0"> {/* Added shrink-0 */}
              {t('searchResults.showingPage', { page: currentPage, totalPages: totalPages, totalResults: totalResults })}
            </div>

            {/* Results Table Container - Allow vertical scroll */}
            <div className="overflow-y-auto flex-grow bg-white shadow rounded-lg"> {/* Added overflow-y-auto flex-grow */}
              {/* Sticky Header Table */}
              <table className="min-w-full divide-y divide-gray-200 text-left">
                <thead className="bg-gray-50 sticky top-0 z-10"> {/* Added sticky top-0 z-10 */}
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                      <input
                        type="checkbox"
                        className="form-checkbox h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        checked={selectedPrograms.length > 0}
                        onChange={() => {
                          if (selectedPrograms.length > 0) {
                            setSelectedPrograms([]);
                          }
                        }}
                        title={t('searchResults.tooltips.deselectAll')}
                      />
                    </th>{/* Checkbox Header */}
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">{t('searchResults.columns.image')}</th> {/* Increased width */}
                    {['Episode Title', 'Series Episode', 'Podcast Episode'].includes(programType) ? (
                      <>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('searchResults.columns.title')}</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Episode Title</th>
                      </>
                    ) : (
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('searchResults.columns.title')}</th>
                    )}
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('searchResults.columns.tmsId')}</th>
                    {/* Make Genres header clickable */}
                    {!hiddenColumns.has('genres') && (
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-blue-600"
                      onClick={() => setIsGenreModalOpen(true)}
                      title={t('searchResults.tooltips.filterByGenre')}
                    >
                      <span className="flex items-center"> {/* Wrap text and icon */}
                        {t('searchResults.columns.genres')}
                        {/* Add filter indicator with icon */}
                        {genreFilterIds.length > 0 && (
                          <span className="ml-1 text-blue-500" title={t('searchResults.tooltips.activeGenreFilters', { count: genreFilterIds.length })}>
                            ({genreFilterIds.length})
                          </span>
                        )}
                        {/* Filter Icon SVG */}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1.5 text-gray-400 group-hover:text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                      </span>
                    </th>
                    )}
                    {!hiddenColumns.has('type') && (
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('searchResults.columns.type')}</th>
                    )}
                    {programType === 'Sports event' ? (
                      <>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-blue-600" onClick={() => setIsGameDateModalOpen(true)}>
                          <span className="flex items-center">
                            {t('searchResults.columns.gameDate')}
                            {gameDate && (
                              <span className="ml-1 text-blue-500" title={t('searchResults.tooltips.activeGameDateFilter')}>
                                (*)
                              </span>
                            )}
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1.5 text-gray-400 group-hover:text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                            </svg>
                          </span>
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('searchResults.columns.gameTime')}</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-blue-600" onClick={() => setIsSeasonModalOpen(true)}>
                          <span className="flex items-center">
                            {t('searchResults.columns.season')}
                            {season && (
                              <span className="ml-1 text-blue-500" title={t('searchResults.tooltips.activeSeasonFilter')}>
                                (*)
                              </span>
                            )}
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1.5 text-gray-400 group-hover:text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                            </svg>
                          </span>
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('searchResults.columns.seasonType')}</th>
                      </>
                    ) : (
                      <>
                        {!hiddenColumns.has('subType') && <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('searchResults.columns.subType')}</th>}
                        {!hiddenColumns.has('year') && <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('searchResults.columns.year')}</th>}
                      </>
                    )}
                    {/* Desc Lang Header */}
                    {!hiddenColumns.has('descLang') && (
                    <th
                      scope="col"
                      className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-blue-600"
                      onClick={() => setIsDescLangModalOpen(true)}
                      title={t('searchResults.tooltips.filterByDescLang')}
                    >
                      <span className="flex items-center">
                        {t('searchResults.columns.descLang')}
                        {descLangFilter.length > 0 && (
                          <span className="ml-1 text-blue-500" title={t('searchResults.tooltips.activeDescLangFilters', { count: descLangFilter.length })}>
                            ({descLangFilter.length})
                          </span>
                        )}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1.5 text-gray-400 group-hover:text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                      </span>
                    </th>
                    )}
                    {/* Title Lang Header */}
                    {!hiddenColumns.has('titleLang') && (
                    <th
                      scope="col"
                      className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-blue-600"
                      onClick={() => setIsTitleLangModalOpen(true)}
                      title={t('searchResults.tooltips.filterByTitleLang')}
                    >
                      <span className="flex items-center">
                        {t('searchResults.columns.titleLang')}
                        {titleLangFilter.length > 0 && (
                          <span className="ml-1 text-blue-500" title={t('searchResults.tooltips.activeTitleLangFilters', { count: titleLangFilter.length })}>
                            ({titleLangFilter.length})
                          </span>
                        )}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1.5 text-gray-400 group-hover:text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                      </span>
                    </th>
                    )}
                    {!hiddenColumns.has('updateId') && (
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('searchResults.columns.updateId')}</th>
                    )}
                    {hasConnectorIds && (
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Connector ID</th>
                    )}
                  </tr>
                </thead>
                {/* Table Body */}
                <tbody className="bg-white divide-y divide-gray-200">
                  {results.map((result) => (
                    <tr
                      key={result.TMSId}
                      className="hover:bg-gray-50" // Removed cursor-pointer from row
                    >
                      {/* Checkbox Cell */}
                      <td className="px-4 py-2 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="form-checkbox h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          checked={selectedPrograms.includes(result.TMSId)}
                          onChange={(e) => handleCheckboxChange(e, result.TMSId)}
                          onClick={(e) => e.stopPropagation()} // Prevent row click when clicking checkbox
                        />
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {result.image_url ? (
                          <img
                            onClick={() => handleResultClick(result)} // Add click handler here
                            className="w-16 aspect-video object-cover cursor-pointer" // Changed size and aspect ratio
                            src={result.image_url}
                            alt={result.title || 'Program image'}
                            onError={(e) => { e.target.src = 'https://panorama.telus.encoreinteractive.ca/offline.png'; }} // Adjusted placeholder size hint
                          />
                        ) : (
                          <div
                            onClick={() => handleResultClick(result)} // Add click handler here
                            className="w-16 aspect-video bg-gray-200 flex items-center justify-center text-xs text-gray-400 cursor-pointer" // Changed size and aspect ratio
                          >
                            {t('searchResults.noImage')}
                          </div>
                        )}
                      </td>
                      {/* Make title clickable */}
                      {['Episode Title', 'Series Episode', 'Podcast Episode'].includes(programType) ? (
                        <>
                          <td
                            className="px-4 py-2 text-sm font-medium text-blue-600 hover:underline cursor-pointer"
                            onClick={() => handleResultClick(result)}
                          >
                            {result.program_title || result.title}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {result.episode_title || 'N/A'}
                          </td>
                        </>
                      ) : (
                        <td
                          className="px-4 py-2 text-sm font-medium text-blue-600 hover:underline cursor-pointer"
                          onClick={() => handleResultClick(result)}
                        >
                          {result.title}
                        </td>
                      )}
                      {/* Use CopyableId for TMS ID */}
                      <td className="px-4 py-2 text-sm text-gray-500">
                        <div className="flex items-center">
                          <CopyableId value={result.TMSId} />
                          <SaveButton 
                            key={result.TMSId}
                            tmsId={result.TMSId} 
                            programType={result.progType} 
                            connectorId={result.connectorId} 
                            buttonClassName="ml-1" 
                          />
                        </div>
                      </td>
                      {!hiddenColumns.has('genres') && <td className="px-4 py-2 text-sm text-gray-500">{result.genres || 'N/A'}</td>}
                      {!hiddenColumns.has('type') && <td className="px-4 py-2 text-sm text-gray-500">{result.progType || 'N/A'}</td>}
                      {programType === 'Sports event' ? (
                        <>
                          <td className="px-4 py-2 text-sm text-gray-500">{result.gameDate || 'N/A'}</td>
                          <td className="px-4 py-2 text-sm text-gray-500">{result.gameTime || 'N/A'}</td>
                          <td className="px-4 py-2 text-sm text-gray-500">{result.season || 'N/A'}</td>
                          <td className="px-4 py-2 text-sm text-gray-500">{result.season_type || 'N/A'}</td>
                        </>
                      ) : (
                        <>
                          {!hiddenColumns.has('subType') && <td className="px-4 py-2 text-sm text-gray-500">{result.subType || 'N/A'}</td>}
                          {!hiddenColumns.has('year') && <td className="px-4 py-2 text-sm text-gray-500">{result.year || 'N/A'}</td>}
                        </>
                      )}
                      {/* Desc Lang Cell */}
                      {!hiddenColumns.has('descLang') && (
                      <td className="px-2 py-2 text-sm text-gray-500">
                        {result.descriptionLang ? (
                          (() => {
                            const colors = getLanguageColors(result.descriptionLang);
                            return (
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${colors.bg} ${colors.text}`}>
                                {result.descriptionLang.toUpperCase()}
                              </span>
                            );
                          })()
                        ) : 'N/A'}
                      </td>
                      )}
                      {/* Title Lang Cell */}
                      {!hiddenColumns.has('titleLang') && (
                      <td className="px-2 py-2 text-sm text-gray-500">
                        {result.titleLang ? (
                          (() => {
                            const colors = getLanguageColors(result.titleLang);
                            return (
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${colors.bg} ${colors.text}`}>
                                {result.titleLang.toUpperCase()}
                              </span>
                            );
                          })()
                        ) : 'N/A'}
                      </td>
                      )}
                      {!hiddenColumns.has('updateId') && (
                      <td className="px-4 py-2 text-sm text-gray-500">{result.updateId || 'N/A'}</td>
                      )}
                       {hasConnectorIds && (
                         <td className="px-4 py-2 text-sm text-gray-500">
                           {result.connectorId ? (
                             <div className="flex items-center">
                               <CopyableId
                                 value={result.connectorId}
                                 onValueClick={() => navigate(`/program/${result.connectorId}`)}
                               />
                               <SaveButton
                                 key={`connector-${result.connectorId}`}
                                 tmsId={result.connectorId}
                                 programType={result.progType}
                                 connectorId={result.connectorId}
                                 buttonClassName="ml-1"
                               />
                             </div>
                           ) : null}
                         </td>
                       )}
                     </tr>
                   ))}
                 </tbody>
               </table>
             </div> {/* Closing Results Table Container */}
           </div> {/* Closing Outer Results Display Div */}
 
           {/* Enhanced Pagination Controls - Now correctly placed inside the fragment but outside the flex container */}
           {totalPages > 1 && (
             // Added ref to pagination controls
             <div className="flex justify-center items-center mt-4 space-x-1 shrink-0" ref={paginationRef}> {/* Added shrink-0, reduced mt */}
               {/* First Page Button */}
               <button
                 onClick={() => handlePageChange(1)}
                 disabled={currentPage === 1}
                 className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                 aria-label="Go to first page"
               >
                 {'<<'} {/* Corrected: Use JSX string literal */}
               </button>
               {/* Previous Page Button */}
               <button
                 onClick={() => handlePageChange(currentPage - 1)}
                 disabled={currentPage === 1}
                 className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                 aria-label="Go to previous page"
               >
                 {'<'} {/* Corrected: Use JSX string literal */}
               </button>
 
               {/* Page Number Buttons */}
               {getPaginationRange().map((pageNumber) => (
                 <button
                   key={pageNumber}
                   onClick={() => handlePageChange(pageNumber)}
                   disabled={currentPage === pageNumber}
                   className={`px-3 py-1 text-sm font-medium border border-gray-300 rounded-md ${
                     currentPage === pageNumber
                       ? 'bg-blue-500 text-white border-blue-500 cursor-default'
                       : 'text-gray-600 bg-white hover:bg-gray-50'
                   }`}
                   aria-label={`Go to page ${pageNumber}`}
                   aria-current={currentPage === pageNumber ? 'page' : undefined}
                 >
                   {pageNumber}
                 </button>
               ))}
 
               {/* Next Page Button */}
               <button
                 onClick={() => handlePageChange(currentPage + 1)}
                 disabled={currentPage === totalPages}
                 className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                 aria-label="Go to next page"
               >
                 {'>'} {/* Corrected: Use JSX string literal */}
               </button>
               {/* Last Page Button */}
               <button
                 onClick={() => handlePageChange(totalPages)}
                 disabled={currentPage === totalPages}
                 className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                 aria-label="Go to last page"
               >
                 {'>>'} {/* Corrected: Use JSX string literal */}
               </button>
             </div>
           )}
         </> // Fragment ends here
       )} {/* Closing parenthesis for main condition */}
 
       {/* Render the Genre Filter Modal */}
       <GenreFilterModal
         isOpen={isGenreModalOpen}
         onClose={() => setIsGenreModalOpen(false)}
         onApply={handleApplyGenres}
         initialSelectedGenres={genreFilterIds} // Pass current filters to initialize modal
       />
 
       {/* Render the Desc Lang Filter Modal */}
       <LanguageFilterModal
         isOpen={isDescLangModalOpen}
         onClose={() => setIsDescLangModalOpen(false)}
         onApply={handleApplyDescLangs}
         initialSelectedLanguages={descLangFilter}
         fetchLanguagesFunction={apiService.getAllDescLangs} // Pass the API function
         title={t('searchResults.tooltips.filterByDescLang')}
       />
 
       {/* Render the Title Lang Filter Modal */}
       <LanguageFilterModal
         isOpen={isTitleLangModalOpen}
         onClose={() => setIsTitleLangModalOpen(false)}
         onApply={handleApplyTitleLangs}
         initialSelectedLanguages={titleLangFilter}
         fetchLanguagesFunction={apiService.getAllTitleLangs} // Pass the API function
         title={t('searchResults.tooltips.filterByTitleLang')}
       />

      <GameDateFilterModal
        isOpen={isGameDateModalOpen}
        onClose={() => setIsGameDateModalOpen(false)}
        onApply={handleApplyGameDate}
        initialGameDate={gameDate}
      />

      <GameSeasonFilterModal
        isOpen={isSeasonModalOpen}
        onClose={() => setIsSeasonModalOpen(false)}
        onApply={handleApplySeason}
        initialSeason={season}
        availableSeasons={availableSeasons}
      />
     </div> // Closes the main container div
   );
 } // Removed semicolon
 
 export default MainSearchResults;
